import { Component, EventEmitter, Output, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { buildUserFormConfig } from '../../config/user-form.config';
import { User } from '../../../core/models/user.model';
import { UserService } from '../../../../services/user/user.service';
import { EoFormComponent, EoFormPanelConfig, STATUS } from 'ngx-eo-web-components';
import { Subject, filter, takeUntil, Subscription } from 'rxjs';
import { CoreService } from '../../../core/core.service';
import { AuthService } from '../../../../services/auth/auth.service';
import { setRolesForDialog } from '../../../shared/components/role-dropdown/role-dropdown.config';

@Component({
  selector: 'app-user-form-dialog',
  templateUrl: './user-form-dialog.component.html',
  styleUrls: ['./user-form-dialog.component.scss'],
})
export class UserFormDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();
  @ViewChild('eoForm', { static: false }) eoForm!: EoFormComponent;

  user?: User;
  visible = false;
  formConfig: EoFormPanelConfig[] = [];

  @Output() success = new EventEmitter();

  constructor(
    private readonly authService: AuthService,
    private userService: UserService,
    private coreService: CoreService
  ) { }

  ngOnInit(): void {
    this.subscriptions = [this.userService.searchCh.data$.subscribe()];
    this.getUserSaveSuccessSub$();
    this.getUserUpdateSuccessSub$();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit($event: any) {
    if (!$event?.user) return;

    const userToStore = {
      ...this.user,
      userName: $event.user.userName,
      email: $event.user.email,
      name: $event.user.name,
      surname: $event.user.surname,
      lockoutEnabled: $event.user.lockoutEnabled || false,
      roles: $event.user.roles,
    };

    (userToStore.userID
      ? this.userService.updateUser(userToStore as User)
      : this.userService.saveUser(userToStore as User)
    ).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private onSubmitSuccess(): void {
    this.userService.init();
    this.visible = false;
    this.showSuccessMessage();
    this.success.emit();
  }

  private initForm() {
    if (!this.eoForm) return;

    this.formConfig = buildUserFormConfig(this.handleRoleRemove.bind(this));

    setTimeout(() => {
        this.eoForm.form.reset();
        this.eoForm.form.enable();
        this.setRoles();

        if (this.user?.userID) {
          this.setUserFormValues(this.user);
        }
      }, 50);
  }

  private setRoles() {
    setRolesForDialog();
  }

  private handleRoleRemove(e: any): void {
    this.formConfig = [...this.formConfig];
  }

  private setUserFormValues(user: User) {
    const { userName, email, name, surname, lockoutEnabled, roles } = user;
    this.setFcValue('userName', userName);
    this.eoForm.getFormControl('user', 'userName')?.disable();
    this.setFcValue('email', email);
    this.setFcValue('name', name);
    this.setFcValue('surname', surname);
    this.setFcValue('lockoutEnabled', lockoutEnabled);
    this.setFcValue('roles', roles);
  }

  private showSuccessMessage(): void {
    this.coreService.showSuccess('Ο χρήστης αποθηκεύτηκε με επιτυχία');
  }

  private setFcValue(formControlName: string, value: any) {
    return this.eoForm?.setValue('user', formControlName, value);
  }

  getHeader(): string {
    return this.user?.userID ? 'Επεξεργασία Χρήστη' : 'Νέος Χρήστης';
  }

  getFormConfig() {
    return this.formConfig;
  }

  private getUserSaveSuccessSub$() {
    this.userService.userSaveCallHandler.status$.pipe(filter((s) => s === STATUS.success), takeUntil(this.destroy$)).subscribe(() => this.onSubmitSuccess());
  }

  private getUserUpdateSuccessSub$() {
    this.userService.userUpdateCallHandler.status$.pipe(filter((s) => s === STATUS.success), takeUntil(this.destroy$)).subscribe(() => this.onSubmitSuccess());
  }

  isSubmitLoading(): boolean {
    const uSaveStatus = this.userService.userSaveCallHandler?.status$?.getValue();
    if (uSaveStatus === STATUS.loading) return true;
    const uUpdStatus = this.userService.userUpdateCallHandler?.status$?.getValue();
    return uUpdStatus === STATUS.loading;
  }

  show(user?: User): void {
    this.user = user;
    this.initForm();
    this.visible = true;
  }
}
